import { apiFetch } from "./api";
import type {
  FinalizeMediaRequest,
  UploadUrlRequest,
  UploadUrlResponse,
} from "@family/shared";

/**
 * Read an image's natural width/height from a File object. Returns null on
 * failure so uploads still succeed (dimensions are metadata, not required).
 */
function readImageDimensions(
  file: File,
): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

/** Max dimension for client-side image compression (preserves aspect ratio) */
const MAX_IMAGE_DIMENSION = 2000;
const COMPRESSION_QUALITY = 0.85;

/**
 * Compress an image file client-side if it exceeds MAX_IMAGE_DIMENSION.
 * Returns the original file if it's small enough or compression fails.
 * Skips HEIC/HEIF (browser can't decode them in canvas).
 */
export async function compressImage(file: File): Promise<File> {
  const mime = file.type.toLowerCase();
  // Skip formats that canvas can't handle
  if (mime === "image/heic" || mime === "image/heif" || mime === "image/gif") {
    return file;
  }

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { naturalWidth: w, naturalHeight: h } = img;

      // Skip if already small enough
      if (w <= MAX_IMAGE_DIMENSION && h <= MAX_IMAGE_DIMENSION) {
        resolve(file);
        return;
      }

      // Calculate new dimensions
      const ratio = Math.min(MAX_IMAGE_DIMENSION / w, MAX_IMAGE_DIMENSION / h);
      const newW = Math.round(w * ratio);
      const newH = Math.round(h * ratio);

      const canvas = document.createElement("canvas");
      canvas.width = newW;
      canvas.height = newH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, newW, newH);

      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) {
            // Compression didn't help, use original
            resolve(file);
            return;
          }
          const compressed = new File([blob], file.name, {
            type: "image/jpeg",
            lastModified: file.lastModified,
          });
          resolve(compressed);
        },
        "image/jpeg",
        COMPRESSION_QUALITY,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}

/**
 * Upload a single photo File:
 *   1. POST /families/:slug/media/upload-url  → signed PUT URL + mediaId
 *   2. PUT file bytes directly to GCS
 *   3. POST /families/:slug/media/:id/finalize with dimensions
 * Returns the mediaId on success.
 *
 * The file is uploaded at its original resolution — the family archive
 * keeps print-quality originals. The API's finalize step generates the
 * web-sized display derivative and thumbnail that viewers actually load,
 * so nothing here needs to shrink the image any more. (compressImage
 * below is still used for issue-report attachments, which have a hard
 * 5 MB cap and no archival value.)
 */
const EXT_MIME: Record<string, string> = {
  mp4: "video/mp4",
  m4v: "video/mp4",
  webm: "video/webm",
  ogv: "video/ogg",
  mov: "video/quicktime",
  mpg: "video/mpeg",
  mpeg: "video/mpeg",
  avi: "video/x-msvideo",
  wmv: "video/x-ms-wmv",
  mkv: "video/x-matroska",
  "3gp": "video/3gpp",
  flv: "video/x-flv",
  ts: "video/mp2t",
  heic: "image/heic",
  heif: "image/heif",
};

function mimeFromName(name: string): string | null {
  const ext = name.toLowerCase().split(".").pop() ?? "";
  return EXT_MIME[ext] ?? null;
}

export async function uploadPhoto(
  familySlug: string,
  file: File,
  opts: {
    onProgress?: (pct: number) => void;
    /** Extra metadata saved with the photo (the scanner's caption, year…). */
    finalize?: Omit<FinalizeMediaRequest, "mimeType" | "width" | "height">;
  } = {},
): Promise<string> {
  // Browsers leave file.type empty for formats they don't know (Windows
  // .wmv files often arrive that way). Fall back to the extension so the
  // file is still stored as a video and sent to the transcoder.
  const mimeType = file.type || mimeFromName(file.name) || "image/jpeg";
  const isVideo = mimeType.startsWith("video/");

  const uploadFile = file;
  const uploadMime = uploadFile.type || mimeType;

  const reqBody: UploadUrlRequest = {
    kind: isVideo ? "video" : "photo",
    mimeType: uploadMime,
    fileSize: uploadFile.size,
  };

  const signed = await apiFetch<UploadUrlResponse>(
    `/families/${familySlug}/media/upload-url`,
    {
      method: "POST",
      body: JSON.stringify(reqBody),
    },
  );

  // PUT directly to GCS. Using XHR instead of fetch so we can report progress.
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", signed.uploadUrl, true);
    xhr.setRequestHeader("Content-Type", uploadMime);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && opts.onProgress) {
        opts.onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status} ${xhr.responseText}`));
    };
    xhr.onerror = () => reject(new Error("Upload network error"));
    xhr.send(uploadFile);
  });

  const dims = isVideo ? null : await readImageDimensions(uploadFile);
  const finalizeBody: FinalizeMediaRequest = {
    ...opts.finalize,
    mimeType: uploadMime,
    ...(dims ? { width: dims.width, height: dims.height } : {}),
  };

  await apiFetch(
    `/families/${familySlug}/media/${signed.mediaId}/finalize`,
    {
      method: "POST",
      body: JSON.stringify(finalizeBody),
    },
  );

  return signed.mediaId;
}
